export type MessageType =
  | "reminder"
  | "receipt"
  | "form_request"
  | "reengagement";

export interface MessageTemplate {
  uuid: string;
  salon_uuid: string;
  title: string;
  text: string;
  message_type: MessageType;
  version: number;
  is_active: boolean;
  created_by: string;
  created_dt: string;
  updated_dt: string;
}

// Per-service automated-message on/off toggles. Each salon_service
// has three switches; all default ON (opt-out). The backend sends a message if
// ANY booked service on an appointment has the corresponding toggle ON.
export interface MessageSettings {
  send_first_reminder: boolean;
  send_second_reminder: boolean;
  send_immediate_marketing_followup: boolean;
}

export const DEFAULT_MESSAGE_SETTINGS: MessageSettings = {
  send_first_reminder: true,
  send_second_reminder: true,
  send_immediate_marketing_followup: true,
};

/**
 * True iff any toggle in `s` differs from `DEFAULT_MESSAGE_SETTINGS`. Used by
 * the new-service wizards to skip the post-create PUT when the user never
 * touched the all-on defaults (the backend defaults to all-on anyway).
 *
 * Kept here so the two wizards (ServiceFormWizard,
 * ProgressiveServicesWizard) can't drift if defaults ever change.
 */
export function hasNonDefaultMessageSettings(s: MessageSettings): boolean {
  return (
    s.send_first_reminder !== DEFAULT_MESSAGE_SETTINGS.send_first_reminder ||
    s.send_second_reminder !== DEFAULT_MESSAGE_SETTINGS.send_second_reminder ||
    s.send_immediate_marketing_followup !==
      DEFAULT_MESSAGE_SETTINGS.send_immediate_marketing_followup
  );
}

export const MESSAGE_TYPE_COLORS: Record<string, string> = {
  reminder: "blue",
  receipt: "green",
  form_request: "burntOrange",
  reengagement: "orange",
};

// Human-readable labels shown to staff when a scheduled message has no
// salon-configured template_title (e.g. the source row was deleted). Kept
// alongside MESSAGE_TYPE_COLORS and the MessageType enum so a future fifth
// type updates all three siblings in one file.
export const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  reminder: "Reminder",
  receipt: "Booking Confirmation Text",
  form_request: "Form Request",
  reengagement: "Marketing Follow-up",
};
